import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import {
  AlertTriangle, ArrowRight, BadgeCheck, Check, ChevronLeft, ChevronRight, RotateCcw, X,
} from "lucide-react";
import { api, CATEGORIES, categoryName } from "@/lib/api";
import { FadeUp, Overline, SectionHead, EASE } from "@/components/site/Reveal";

const STEPS = [
  {
    key: "ownership",
    q: "Do you own the home?",
    hint: null,
    options: [["own", "Yes — I own it"], ["renting", "No — I rent it"], ["other", "It's complicated"]],
  },
  {
    key: "home_type",
    q: "Is this for an existing home or a new build?",
    hint: null,
    options: [["existing", "Existing home"], ["new", "New build"]],
  },
  {
    key: "property_value",
    q: "Is the property valued under $3 million?",
    hint: "One of the Solar Homes eligibility rules.",
    options: [["yes", "Yes"], ["no", "No"], ["unsure", "Not sure"]],
  },
  {
    key: "income",
    q: "Is your combined household income within the Solar Homes threshold?",
    hint: "Current thresholds are published at solar.vic.gov.au.",
    options: [["yes", "Yes"], ["no", "No"], ["unsure", "Not sure"]],
  },
  {
    key: "replacing_3yr",
    q: "Are you replacing a system that's 3+ years old?",
    hint: "Some VEU discounts only apply to replacements.",
    options: [["yes", "Yes"], ["no", "No"], ["unsure", "Not sure"]],
  },
  {
    key: "category",
    q: "What are you most interested in?",
    hint: null,
    options: CATEGORIES.map((c) => [c.slug, c.name]),
  },
];

const STATUS_STYLE = {
  likely: { icon: BadgeCheck, box: "border-forest-400/40 bg-forest-500/15", text: "text-forest-100", label: "You're likely eligible" },
  maybe: { icon: AlertTriangle, box: "border-amber-400/40 bg-amber-400/10", text: "text-amber-200", label: "Possibly eligible" },
  unlikely: { icon: X, box: "border-bone/15 bg-bone/5", text: "text-bone/50", label: "Probably not eligible" },
};

function Checker() {
  const [phase, setPhase] = useState("intro"); // intro | questions | results
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [results, setResults] = useState(null);
  const [busy, setBusy] = useState(false);

  const pick = (key, value) => {
    const next = { ...answers, [key]: value };
    setAnswers(next);
    if (step + 1 < STEPS.length) {
      setTimeout(() => setStep(step + 1), 180);
    } else {
      setBusy(true);
      api
        .post("/eligibility-check", next)
        .then((d) => {
          setResults(d.data.results);
          setPhase("results");
        })
        .catch(() => setResults([]))
        .finally(() => setBusy(false));
    }
  };

  const reset = () => {
    setPhase("intro");
    setStep(0);
    setAnswers({});
    setResults(null);
  };

  const progress = phase === "results" ? 100 : (step / STEPS.length) * 100;
  const current = STEPS[step];

  return (
    <div className="mx-auto max-w-2xl" data-testid="eligibility-checker">
      <div className="h-1 w-full overflow-hidden rounded-full bg-bone/15">
        <div
          className="h-full rounded-full bg-terra transition-all duration-500"
          style={{ width: `${progress}%` }}
          data-testid="checker-progress"
        />
      </div>

      <div className="mt-8 min-h-[300px]">
        {phase === "intro" && (
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, ease: EASE }}>
            <h3 className="font-display text-2xl font-extrabold text-bone sm:text-3xl">
              Six questions. Sixty seconds. Honest answers.
            </h3>
            <p className="mt-3 text-base leading-relaxed text-bone/70">
              We check your answers against the published rules of each program
              — Solar Homes, VEU and federal STCs — and tell you what's likely
              to apply to your home.
            </p>
            <button
              onClick={() => setPhase("questions")}
              data-testid="checker-start"
              className="mt-8 inline-flex items-center gap-2 rounded-full bg-terra px-7 py-4 text-sm font-bold text-bone transition-colors hover:bg-terra-dark"
            >
              Start the check <ArrowRight className="h-4 w-4" />
            </button>
          </motion.div>
        )}

        {phase === "questions" && current && (
          <AnimatePresence mode="wait">
            <motion.div
              key={step}
              initial={{ opacity: 0, x: 40 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -40 }}
              transition={{ duration: 0.35, ease: EASE }}
              data-testid={`checker-step-${step}`}
            >
              <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-terra">
                Question {step + 1} of {STEPS.length}
              </p>
              <h3 className="font-display mt-3 text-2xl font-bold text-bone">{current.q}</h3>
              {current.hint && <p className="mt-2 text-sm text-bone/60">{current.hint}</p>}
              <div className="mt-7 grid gap-3">
                {current.options.map(([value, label]) => {
                  const selected = answers[current.key] === value;
                  return (
                    <button
                      key={value}
                      onClick={() => pick(current.key, value)}
                      disabled={busy}
                      data-testid={`checker-option-${current.key}-${value}`}
                      className={`flex items-center justify-between rounded-lg border px-5 py-4 text-left text-sm font-semibold transition-colors ${
                        selected
                          ? "border-terra bg-terra/20 text-bone"
                          : "border-bone/20 bg-forest-800/60 text-bone/85 hover:border-terra hover:text-bone"
                      }`}
                    >
                      {label}
                      <ChevronRight className="h-4 w-4 text-terra" />
                    </button>
                  );
                })}
              </div>
              <button
                onClick={() => setStep(Math.max(0, step - 1))}
                data-testid="checker-back"
                className="mt-6 inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-[0.16em] text-bone/50 hover:text-bone"
              >
                <ChevronLeft className="h-4 w-4" /> Back
              </button>
            </motion.div>
          </AnimatePresence>
        )}

        {phase === "results" && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, ease: EASE }} data-testid="checker-results">
            <h3 className="font-display text-2xl font-extrabold text-bone sm:text-3xl">
              Here's what your answers suggest.
            </h3>
            {(!results || results.length === 0) ? (
              <p className="mt-4 text-bone/70">
                No current programs match those answers — but programs change
                often. Ask us what's coming up.
              </p>
            ) : (
              <div className="mt-6 space-y-4">
                {results.map((r, i) => {
                  const style = STATUS_STYLE[r.status] || STATUS_STYLE.maybe;
                  const Icon = style.icon;
                  return (
                    <div
                      key={r.id || i}
                      className={`rounded-lg border p-5 ${style.box}`}
                      data-testid={`checker-result-${i}`}
                    >
                      <div className="flex items-center justify-between gap-3">
                        <span className={`inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.16em] ${style.text}`}>
                          <Icon className="h-4 w-4" /> {style.label}
                        </span>
                        <span className="font-display text-lg font-extrabold text-bone">{r.amount_label}</span>
                      </div>
                      <p className="font-display mt-2 text-lg font-bold text-bone">{r.title}</p>
                      <p className="mt-0.5 text-xs font-semibold uppercase tracking-[0.14em] text-bone/50">{r.program}</p>
                      <ul className="mt-3 space-y-1.5">
                        {r.reasons.map((reason, j) => (
                          <li key={j} className="flex items-start gap-2 text-sm text-bone/75">
                            {r.status === "likely" ? (
                              <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-terra" />
                            ) : (
                              <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-bone/40" />
                            )}
                            {reason}
                          </li>
                        ))}
                      </ul>
                    </div>
                  );
                })}
              </div>
            )}
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <Link
                to={`/contact?interest=${encodeURIComponent(categoryName(answers.category || ""))}`}
                data-testid="checker-cta-quote"
                className="inline-flex items-center gap-2 rounded-full bg-terra px-7 py-4 text-sm font-bold text-bone transition-colors hover:bg-terra-dark"
              >
                Request a quote for your {categoryName(answers.category || "home").toLowerCase()} <ArrowRight className="h-4 w-4" />
              </Link>
              <button
                onClick={reset}
                data-testid="checker-reset"
                className="inline-flex items-center gap-2 rounded-full border border-bone/30 px-6 py-4 text-sm font-bold text-bone transition-colors hover:border-terra hover:text-terra"
              >
                <RotateCcw className="h-4 w-4" /> Run it again
              </button>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
}

export default function RebatesPage() {
  const [rebates, setRebates] = useState(null);

  useEffect(() => {
    api
      .get("/rebates")
      .then((d) => setRebates(d.data))
      .catch(() => setRebates([]));
  }, []);

  return (
    <div className="pt-28 sm:pt-32" data-testid="rebates-page">
      <header className="mx-auto max-w-7xl px-6 pb-14">
        <div className="max-w-3xl">
          <Overline className="text-terra">Victorian &amp; federal incentives</Overline>
          <h1 className="font-display mt-3 text-4xl font-extrabold tracking-tight text-forest-700 sm:text-5xl lg:text-6xl" data-testid="rebates-title">
            Rebates that come off your invoice, not your weekends.
          </h1>
          <p className="mt-5 text-base leading-relaxed text-moss sm:text-lg">
            Every program below is applied by us, directly in your quote. You
            never chase paperwork or wait for reimbursements.
          </p>
        </div>
      </header>

      <section className="mx-auto max-w-7xl px-6 pb-20" data-testid="rebates-list">
        {rebates === null ? (
          <div className="grid gap-6 md:grid-cols-2">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="h-52 animate-pulse rounded-lg bg-sage" />
            ))}
          </div>
        ) : (
          <div className="grid gap-6 md:grid-cols-2">
            {rebates.map((r, i) => (
              <FadeUp key={r.id} delay={i * 0.06} className="h-full">
                <div
                  className="clip-notch flex h-full flex-col border border-forest-700/10 bg-bone-dark p-7"
                  data-testid={`rebate-card-${r.slug}`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-moss">{r.program}</p>
                      <h2 className="font-display mt-2 text-xl font-bold text-forest-700">{r.title}</h2>
                    </div>
                    <p className="font-display whitespace-nowrap text-2xl font-extrabold text-terra">{r.amount_label}</p>
                  </div>
                  <p className="mt-4 text-sm leading-relaxed text-moss">{r.summary}</p>
                  <ul className="mt-4 flex-1 space-y-2">
                    {r.bullets.map((b, j) => (
                      <li key={j} className="flex items-start gap-2 text-sm text-forest-700">
                        <Check className="mt-0.5 h-4 w-4 shrink-0 text-terra" /> {b}
                      </li>
                    ))}
                  </ul>
                  <div className="mt-5 flex flex-wrap gap-2">
                    {r.categories.map((c) => (
                      <Link
                        key={c}
                        to={`/products/${c}`}
                        className="rounded-full border border-forest-700/20 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.14em] text-forest-700 transition-colors hover:border-terra hover:text-terra"
                      >
                        {categoryName(c)}
                      </Link>
                    ))}
                  </div>
                </div>
              </FadeUp>
            ))}
          </div>
        )}
      </section>

      <section className="grain bg-forest-800 text-bone" data-testid="checker-section">
        <div className="relative z-10 mx-auto max-w-7xl px-6 py-20 md:py-28">
          <SectionHead
            light
            overline="Eligibility checker"
            title="Find out what you qualify for"
            className="mb-14"
          />
          <Checker />
          <p className="mx-auto mt-14 max-w-2xl text-center text-xs leading-relaxed text-bone/50" data-testid="rebates-disclaimer">
            This checker provides guidance only — it is not an official rebate
            approval. Program values and rules are set by Solar Victoria and
            the Australian Government and change from time to time. Always
            confirm current rules at{" "}
            <a
              href="https://www.solar.vic.gov.au"
              target="_blank"
              rel="noopener noreferrer"
              className="underline underline-offset-2 hover:text-terra"
              data-testid="rebates-disclaimer-link"
            >
              solar.vic.gov.au
            </a>
            .
          </p>
        </div>
      </section>
    </div>
  );
}
